"use client"

import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { Volume2, VolumeX, ChevronLeft, ChevronRight } from "lucide-react"
import { Button } from "./ui/button"

export default function HeroSection() {
  const [isSoundOn, setIsSoundOn] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)

  // Array of hero images
  const heroImages = [
    "/WebBanner_1.png",
    "/WebBanner_2.png", 
    "/WebBanner_3.png"
  ]

  // Array of corresponding URLs for each image
  const imageUrls = [
    "/shoproduct/furniture",
    "/shoproduct/tableware",
    "/shoproduct/decor"
  ]

  // Auto-slide functionality
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isTransitioning) {
        setCurrentImageIndex((prevIndex) => 
          prevIndex === heroImages.length - 1 ? 0 : prevIndex + 1
        )
      }
    }, 5000) // Change image every 5 seconds

    return () => clearInterval(interval)
  }, [heroImages.length, isTransitioning])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        goToPrevious()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        goToNext()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isTransitioning])

  const toggleSound = () => {
    setIsSoundOn(!isSoundOn)
    // Sound logic would go here
  }

  const goToSlide = (index) => {
    if (!isTransitioning && index !== currentImageIndex) {
      setIsTransitioning(true)
      setCurrentImageIndex(index)
      setTimeout(() => setIsTransitioning(false), 1000) // Match transition duration
    }
  }

  const goToPrevious = () => {
    if (!isTransitioning) {
      setIsTransitioning(true)
      setCurrentImageIndex((prevIndex) => 
        prevIndex === 0 ? heroImages.length - 1 : prevIndex - 1
      )
      setTimeout(() => setIsTransitioning(false), 1000) // Match transition duration
    }
  }

  const goToNext = () => {
    if (!isTransitioning) {
      setIsTransitioning(true)
      setCurrentImageIndex((prevIndex) => 
        prevIndex === heroImages.length - 1 ? 0 : prevIndex + 1
      )
      setTimeout(() => setIsTransitioning(false), 1000) // Match transition duration
    }
  }

  const handleCTAClick = () => {
    const targetUrl = imageUrls[currentImageIndex]
    window.open(targetUrl, '_blank')
  }

  return (
    <section className="hero-section-mobile mobile-full-height relative h-screen w-full overflow-hidden">
      {/* Background Image Slider */}
      <div className="absolute top-0 left-0 h-full w-full">
        {heroImages.map((image, index) => (
          <motion.div
            key={index}
            className="absolute top-0 left-0 h-full w-full"
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: index === currentImageIndex ? 1 : 0,
              scale: index === currentImageIndex ? 1 : 1.1
            }}
            transition={{ duration: 1, ease: "easeInOut" }}
          >
            <img 
              src={image} 
              alt={`Hero slide ${index + 1}`}
              className="h-full w-full object-cover"
            />
          </motion.div>
        ))}
      </div>

      {/* Navigation Arrows */}
      <motion.button
        onClick={goToPrevious}
        disabled={isTransitioning}
        className={`absolute left-2 sm:left-4 top-1/2 z-30 transform -translate-y-1/2 flex h-10 w-10 sm:h-12 sm:w-12 md:h-14 md:w-14 items-center justify-center rounded-full border border-white/20 bg-black/30 backdrop-blur-sm transition-all duration-300 hover:bg-white/20 hover:border-white/40 hover:scale-110 ${
          isTransitioning ? 'opacity-50 cursor-not-allowed' : 'opacity-80 hover:opacity-100'
        }`}
        whileHover={{ scale: isTransitioning ? 1 : 1.1 }}
        whileTap={{ scale: isTransitioning ? 1 : 0.95 }}
        aria-label="Previous image"
      >
        <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7 text-white" />
      </motion.button>
      
      <motion.button
        onClick={goToNext}
        disabled={isTransitioning}
        className={`absolute right-2 sm:right-4 top-1/2 z-30 transform -translate-y-1/2 flex h-10 w-10 sm:h-12 sm:w-12 md:h-14 md:w-14 items-center justify-center rounded-full border border-white/20 bg-black/30 backdrop-blur-sm transition-all duration-300 hover:bg-white/20 hover:border-white/40 hover:scale-110 ${
          isTransitioning ? 'opacity-50 cursor-not-allowed' : 'opacity-80 hover:opacity-100'
        }`}
        whileHover={{ scale: isTransitioning ? 1 : 1.1 }}
        whileTap={{ scale: isTransitioning ? 1 : 0.95 }}
        aria-label="Next image"
      >
        <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7 text-white" />
      </motion.button>

      {/* Slide Indicators */}
      <div className="absolute bottom-16 sm:bottom-20 left-1/2 z-30 transform -translate-x-1/2 flex space-x-2 sm:space-x-3">
        {heroImages.map((_, index) => (
          <motion.button
            key={index}
            onClick={() => goToSlide(index)}
            disabled={isTransitioning}
            className={`h-2 w-6 sm:h-3 sm:w-8 md:w-10 rounded-full transition-all duration-300 border border-white/30 ${
              index === currentImageIndex 
                ? 'bg-white shadow-lg' 
                : 'bg-white/30 hover:bg-white/50'
            } ${
              isTransitioning ? 'cursor-not-allowed' : 'hover:scale-110'
            }`}
            whileHover={{ scale: isTransitioning ? 1 : 1.1 }}
            whileTap={{ scale: isTransitioning ? 1 : 0.9 }}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Overlay */}
      <div className="absolute inset-0 bg-black/30 z-10" />

      {/* Content */}
      <div className="relative z-20 flex h-full w-full flex-col items-center justify-end pb-32 sm:pb-40 md:pb-48 px-4 sm:px-6 text-center text-white">

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <Button
            size="lg"
            variant="outline"
            onClick={handleCTAClick}
            className="hero-button-mobile mobile-button-primary rounded-none border-white bg-transparent px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base md:text-lg text-white hover:!bg-amber-700 hover:!text-white hover:!border-amber-700 cursor-pointer transition-all duration-300"
          >
            Browse Collection
          </Button>
        </motion.div>

        {/* Sound Toggle */}
        <motion.button
          onClick={toggleSound}
          className="absolute bottom-4 right-4 sm:bottom-6 sm:right-6 md:bottom-8 md:right-8 z-30 flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-full bg-white/10 backdrop-blur-sm transition hover:bg-white/20"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          aria-label={isSoundOn ? "Mute ambient sound" : "Play ambient sound"}
        >
          {isSoundOn ? <Volume2 className="h-5 w-5 sm:h-6 sm:w-6 text-white" /> : <VolumeX className="h-5 w-5 sm:h-6 sm:w-6 text-white" />}
        </motion.button>
      </div>
    </section>
  )
} 